import { DecimalPipe, registerLocaleData } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import es from '@angular/common/locales/es';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ActivatedRouteSnapshot, RouterStateSnapshot, UrlSerializer } from '@angular/router';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { IConfig, NgxMaskModule } from 'ngx-mask';
import { GlobalErrorHandler } from './app-errorhandler.service';
import { AppInitService } from './app-initializer.service';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { DesignWizardComponent } from './design-wizard/design-wizard.component';
import { GeneralTermsOfUseComponent } from './general-terms-of-use/general-terms-of-use.component';
import { MainModule } from './main/main.module';
import { ErrorComponent } from './public/error/error.component';
import { PublicModule } from './public/public.module';
import { CustomTranslationsLoader } from './shared/custom-translations-loader';
import { RedirectGuard } from './shared/guards/redirect.guard';
import { HTTP_INTERCEPTORS_PROVIDERS } from './shared/interceptors';
import { LowerCaseUrlSerializer } from './shared/lower-case-url-serializer';
import { NotificationsModule } from './shared/notifications/notifications.module';
import { LocalDatePipe } from './shared/pipes/local-date.pipe';
import { LocalNumberPipe } from './shared/pipes/local-number.pipe';
import { NoCommaPipe } from './shared/pipes/no-comma.pipe';
import { SharedModule } from './shared/shared.module';
import { WINDOW_PROVIDERS } from './shared/window.provider';
import { GetAddressGpsPipe } from '@app/shared/pipes/get-address-gps.pipe';
import { GetTariffTypePipe } from './shared/pipes/get-tariff-type.pipe';
import { VersionCheckDialogComponent } from './version-check/dialog/version-check-dialog/version-check-dialog.component';

registerLocaleData(es);

export function appInitFactory(appInitService: AppInitService) {
  return () => appInitService.init();
}

export function resolveExternalURL(route: ActivatedRouteSnapshot) {
  return (route.data as any).externalUrl;
}

export const options: Partial<IConfig> | (() => Partial<IConfig>) = {};

@NgModule({
  imports: [
    NgxMaskModule.forRoot(options),
    BrowserModule.withServerTransition({ appId: 'ng-cli-universal' }),
    BrowserAnimationsModule,
    AppRoutingModule,
    SharedModule,
    NotificationsModule,
    MainModule,
    PublicModule,
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: CustomTranslationsLoader,
      },
    }),
    NgIdleKeepaliveModule.forRoot(),
  ],
  declarations: [AppComponent, ErrorComponent, DesignWizardComponent, GeneralTermsOfUseComponent, VersionCheckDialogComponent],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: appInitFactory,
      deps: [AppInitService],
      multi: true,
    },
    {
      provide: UrlSerializer,
      useClass: LowerCaseUrlSerializer,
    },
    {
      provide: 'externalUrlRedirectResolver',
      useValue: (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => (window.location.href = resolveExternalURL(route)),
    },
    WINDOW_PROVIDERS,
    CookieService,
    HTTP_INTERCEPTORS_PROVIDERS,
    RedirectGuard,
    DecimalPipe,
    NoCommaPipe,
    LocalDatePipe,
    LocalNumberPipe,
    GetAddressGpsPipe,
    GetTariffTypePipe,
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
